import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Plus, Save, Trash2, ToggleLeft, ToggleRight, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  adminCreateBudgetCategory,
  adminDeleteBudgetCategory,
  adminListBudgetCategories,
  adminUpdateBudgetCategory,
} from "../../lib/adminApi";
import { normalizeDetail } from "../../lib/errors";

const EMPTY = { kind: "expense", group: "", name: "", order: 100, is_active: true };

export function AdminBudgetCategoriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await adminListBudgetCategories());
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load categories."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const grouped = useMemo(() => {
    const byKindGroup = { income: {}, expense: {} };
    rows.forEach((r) => {
      const k = r.kind;
      byKindGroup[k][r.group] = byKindGroup[k][r.group] || [];
      byKindGroup[k][r.group].push(r);
    });
    Object.values(byKindGroup).forEach((groups) => {
      Object.values(groups).forEach((arr) => arr.sort((a, b) => a.order - b.order));
    });
    return byKindGroup;
  }, [rows]);

  function beginAdd() {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(true);
  }

  function beginEdit(row) {
    setEditingId(row.id);
    setForm({
      kind: row.kind,
      group: row.group,
      name: row.name,
      order: row.order,
      is_active: row.is_active,
    });
    setShowForm(true);
  }

  function cancel() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.group.trim() || !form.name.trim()) {
      toast.error("Group and Name are required.");
      return;
    }
    try {
      if (editingId) await adminUpdateBudgetCategory(editingId, form);
      else await adminCreateBudgetCategory(form);
      toast.success(editingId ? "Updated." : "Added.");
      cancel();
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    }
  }

  async function handleToggle(row) {
    try {
      await adminUpdateBudgetCategory(row.id, { is_active: !row.is_active });
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't toggle."));
    }
  }

  async function handleDelete(row) {
    if (row.is_system) {
      toast.error("System items can be deactivated but not deleted.");
      return;
    }
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await adminDeleteBudgetCategory(row.id);
      toast.success("Deleted.");
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't delete."));
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="admin-budget-cats-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted hover:text-gold">
        <ArrowLeft className="h-3 w-3" /> Admin dashboard
      </Link>
      <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold">Master Data</div>
          <h1 className="mt-1 font-serif text-3xl text-text">Budget Categories</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Income + expense line items shown on every member&apos;s monthly budget page.
            System defaults can be deactivated but not deleted; custom rows are fully editable.
          </p>
        </div>
        <Button onClick={beginAdd} data-testid="add-budget-cat">
          <Plus className="mr-2 h-4 w-4" /> Add category
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6" data-testid="budget-cat-form">
          <CardHeader>
            <CardTitle className="text-base">
              {editingId ? "Edit category" : "Add category"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cat-kind">Kind</Label>
                <select
                  id="cat-kind"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text"
                  data-testid="cat-kind"
                >
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cat-group">Group</Label>
                <Input
                  id="cat-group"
                  value={form.group}
                  onChange={(e) => setForm({ ...form, group: e.target.value })}
                  placeholder={form.kind === "income" ? "e.g. Employment Income" : "e.g. Household Expenses"}
                  required
                  data-testid="cat-group"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cat-name">Name</Label>
                <Input
                  id="cat-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="cat-name"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cat-order">Order</Label>
                <Input
                  id="cat-order"
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 100 })}
                  data-testid="cat-order"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-text sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="cat-active"
                />
                Active (visible on member budgets)
              </label>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" data-testid="cat-save">
                  <Save className="mr-2 h-4 w-4" /> {editingId ? "Update" : "Save"}
                </Button>
                <Button type="button" variant="outline" onClick={cancel} data-testid="cat-cancel">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-center text-sm text-muted">Loading…</p>
      ) : (
        <>
          {(["income", "expense"]).map((kind) => (
            <Card key={kind} className="mb-6">
              <CardHeader>
                <CardTitle className="text-base capitalize">{kind}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {Object.keys(grouped[kind]).length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted">
                    No {kind} categories yet.
                  </p>
                ) : (
                  Object.entries(grouped[kind]).map(([group, items]) => (
                    <div key={group} className="border-t border-border first:border-t-0">
                      <div className="bg-elevated/40 px-4 py-2 text-xs uppercase tracking-widest text-muted">
                        {group} · {items.length}
                      </div>
                      <ul className="divide-y divide-border">
                        {items.map((r) => (
                          <li
                            key={r.id}
                            className="flex items-center justify-between gap-3 px-4 py-2"
                            data-testid={`budget-cat-${r.id}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm text-text">
                                {r.name}
                                {!r.is_active && (
                                  <span className="ml-2 rounded-full border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-red-300">
                                    inactive
                                  </span>
                                )}
                                {r.is_system && (
                                  <span className="ml-2 rounded-full border border-border bg-elevated px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-muted">
                                    system
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] uppercase tracking-widest text-muted">
                                Order {r.order}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleToggle(r)}
                                className="rounded-md p-1 text-muted hover:text-gold"
                                aria-label={r.is_active ? "Deactivate" : "Activate"}
                                data-testid={`toggle-${r.id}`}
                              >
                                {r.is_active
                                  ? <ToggleRight className="h-4 w-4" />
                                  : <ToggleLeft className="h-4 w-4" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => beginEdit(r)}
                                className="rounded-md p-1 text-muted hover:text-gold"
                                aria-label="Edit"
                                data-testid={`edit-${r.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(r)}
                                className={`rounded-md p-1 ${
                                  r.is_system ? "cursor-not-allowed text-muted/40" : "text-muted hover:text-red-400"
                                }`}
                                aria-label="Delete"
                                disabled={r.is_system}
                                data-testid={`delete-${r.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

export default AdminBudgetCategoriesPage;
